import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import BeatCard from "@/components/BeatCard";
import EyeballMascot from "@/components/EyeballMascot";
import { Search, Sparkles, Headphones } from "lucide-react";

export default function Marketplace() {
  const [beats, setBeats] = useState([]);
  const [q, setQ] = useState("");
  const [activeGenre, setActiveGenre] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/beats");
        setBeats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const genres = useMemo(() => {
    const set = new Set(beats.map((b) => b.genre).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [beats]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return beats.filter((b) => {
      if (activeGenre !== "All" && (b.genre || "").toLowerCase() !== activeGenre.toLowerCase()) return false;
      if (!ql) return true;
      return (
        (b.title || "").toLowerCase().includes(ql) ||
        (b.genre || "").toLowerCase().includes(ql) ||
        (b.tags || []).some((t) => t.toLowerCase().includes(ql))
      );
    });
  }, [beats, q, activeGenre]);

  return (
    <div className="pb-32" data-testid="marketplace-page">
      {/* Hero */}
      <section className="hero-shapes" data-testid="hero-section">
        <span className="hero-shape-pink" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="max-w-2xl fade-up">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-[var(--pink)] text-[var(--ink)] border-2 border-[var(--ink)] shadow-[2px_2px_0_0_var(--ink)]">
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-xs font-bold tracking-wider uppercase">
                Premium Instrumentals
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Loud beats.
              <br />
              <span className="text-[var(--primary)]">Bigger</span> <span className="text-[var(--pink)]">vibes</span>.
            </h1>
            <p className="mt-5 text-[var(--text-dim)] text-base sm:text-lg max-w-xl">
              Hand-crafted instrumentals by SonnyMane. Preview 30 seconds. Cop the license. Make the song.
            </p>
            <div className="mt-7 flex items-center gap-2 card-flat p-2 max-w-xl">
              <Search className="w-5 h-5 ml-2 text-[var(--text-dim)]" />
              <input
                type="text"
                placeholder="Search title, genre, vibe…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[var(--text)] placeholder:text-[var(--text-dim)] py-2 px-1"
                data-testid="marketplace-search-input"
              />
              <a
                href="#beats"
                className="btn-primary px-4 py-2 text-sm hidden sm:flex items-center gap-1.5"
                data-testid="hero-browse-btn"
              >
                <Headphones className="w-4 h-4" /> Browse
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Eyeball mascot - tracks cursor */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col items-center" data-testid="eyeball-section">
        <EyeballMascot />
        <p className="font-display text-sm mt-4 text-[var(--text-dim)] uppercase tracking-[0.25em]">
          we see you scrolling
        </p>
      </section>

      {/* Genres */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10" id="beats">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="genre-filter-list">
          {genres.map((g) => (
            <button
              key={g}
              onClick={() => setActiveGenre(g)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase font-bold tracking-wider transition-all border-2 border-[var(--ink)] ${
                activeGenre === g
                  ? "bg-[var(--primary)] text-[var(--ink)] shadow-[2px_2px_0_0_var(--ink)]"
                  : "bg-[var(--bg-surface)] text-[var(--text-dim)] hover:text-[var(--text)]"
              }`}
              data-testid={`genre-filter-${g.toLowerCase()}`}
            >
              {g}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card-base aspect-[3/4] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 card-flat" data-testid="marketplace-empty-state">
            <p className="text-[var(--text-dim)]">No beats found. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="beats-grid">
            {filtered.map((b) => (
              <BeatCard key={b.id} beat={b} />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-5 sm:px-8 mt-24 pt-10 border-t-2 border-[var(--ink)] text-sm text-[var(--text-dim)]">
        <div className="flex flex-col sm:flex-row justify-between gap-4 pb-8">
          <p>© {new Date().getFullYear()} SonnyManeBeatz. All rights reserved.</p>
          <p>Powered by PayPal · Non-exclusive licensing</p>
        </div>
      </footer>
    </div>
  );
}
